import { AxiosRequestConfig } from "axios";
import merge from "lodash/merge";
import { GranularCatalogConfig } from "@granular/fabric-catalog-helpers";

import fieldResource from "./v2/field";
import farmResource from "./v2/farm";
import farmIdResource from "./v2/farm/[farmId]";
import historicalResource from "./v2/field/historical";
import fieldIdResource from "./v2/field/[fieldId]";
import attributeSyncKeysResource from "./v0/field-attribute/sync-keys";
import revisionSyncKeysResource from "./v0/field-revision/sync-keys";

export type GranularCatalogFieldsSvcInstance = {
  fieldsSvc: FieldsSvcInstance;
};

export type FieldsSvcInstance = {
  getFields: (
    params: Parameters<typeof historicalResource.GET>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof historicalResource.GET>;
  addField: (
    field: Parameters<typeof fieldResource.POST>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof fieldResource.POST>;
  deleteField: (
    fieldId: Parameters<typeof fieldIdResource.DELETE>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof fieldIdResource.DELETE>;
  getFarms: (
    params: Parameters<typeof farmResource.GET>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof farmResource.GET>;
  addFarm: (
    farm: Parameters<typeof farmResource.POST>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof farmResource.POST>;
  updateFarm: (
    farm: Parameters<typeof farmIdResource.PUT>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof farmIdResource.PUT>;
  deleteFarm: (
    farmId: Parameters<typeof farmIdResource.DELETE>[0],
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof farmIdResource.DELETE>;
  syncFieldAttributes: (
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof attributeSyncKeysResource.GET>;
  syncFieldRevisions: (
    axiosRequestConfig?: AxiosRequestConfig,
  ) => ReturnType<typeof revisionSyncKeysResource.GET>;
};

const createFieldsSvcInstance = (
  catalogConfig: GranularCatalogConfig,
  instanceRequestConfig?: AxiosRequestConfig,
): FieldsSvcInstance => ({
  getFields: (params, axiosRequestConfig) =>
    historicalResource.GET(
      params,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  addField: (field, axiosRequestConfig) =>
    fieldResource.POST(
      field,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  deleteField: (fieldId, axiosRequestConfig) =>
    fieldIdResource.DELETE(
      fieldId,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  getFarms: (params, axiosRequestConfig) =>
    farmResource.GET(
      params,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  addFarm: (farm, axiosRequestConfig) =>
    farmResource.POST(
      farm,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  updateFarm: (farm, axiosRequestConfig) =>
    farmIdResource.PUT(
      farm,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  deleteFarm: (farmId, axiosRequestConfig) =>
    farmIdResource.DELETE(
      farmId,
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  syncFieldAttributes: (axiosRequestConfig) =>
    attributeSyncKeysResource.GET(
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
  syncFieldRevisions: (axiosRequestConfig) =>
    revisionSyncKeysResource.GET(
      catalogConfig,
      merge(instanceRequestConfig, axiosRequestConfig),
    ),
});

export default createFieldsSvcInstance;
